// ── Call Center — shared frontend contract ────────────────────────────────
//
// Hand-synced with pos-api/src/pos/call-center/callCenter.types.ts, following
// the convention already established by service.ts and receipts-detail.ts.
// pos-api has no workspace dependency on @matix/types (it compiles under Node16
// resolution), so the two files are kept in step by hand. Change one, change
// the other.
//
// The cashier app and the admin app both consume these: the agent's order
// builder and dashboard, the branch's pending list and monitor, and the admin
// settings/orders/analytics screens all speak this vocabulary.

/**
 * Where a call-center order sits in its lifecycle.
 *
 *     new ──▶ preparing ──▶ ready ──▶ out_for_delivery ──▶ delivered
 *      │          │           │              │
 *      └──────────┴───────────┴──────────────┴──────────▶ cancelled
 *
 * `preparing` and `ready` are written by the KITCHEN bumping its own tickets;
 * no human types them. `delivered` is written by the CASHIER settling.
 */
export type CallCenterStatus =
  | 'new'
  | 'preparing'
  | 'ready'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled';

/** The statuses a pending list and a dashboard's "open" filter show. */
export const CC_OPEN_STATUSES: readonly CallCenterStatus[] = [
  'new',
  'preparing',
  'ready',
  'out_for_delivery',
];

export type CallCenterEventSource =
  | 'call_center'
  | 'kds'
  | 'cashier'
  | 'admin'
  | 'system';

export interface CallCenterSettings {
  isEnabled: boolean;
  defaultOrderType: number;
  defaultDeliveryFee: number;
  requireAddress: boolean;
  defaultBranchId: number | null;
  allowPriceEdit: boolean;
  maxDiscountPercent: number;
  pendingPaymentTypeId: number | null;
  settleRequiresReady: boolean;
  allowPrepaid: boolean;
  promiseMinutes: number;
  warnSeconds: number;
  criticalSeconds: number;
  allowCancelAfterStart: boolean;
  requireCancelReason: boolean;
  pollSeconds: number;
}

export interface CallCenterBranchOption {
  id: number;
  code: string;
  name: string;
  nameAr: string | null;
  openCount: number;
}

export interface CallCenterPaymentType {
  id: number;
  code: number;
  name: string;
  nameAr: string | null;
  isCash: boolean;
}

export interface CallCenterBootstrap {
  /** False until migration 046 has been applied to this tenant. */
  provisioned: boolean;
  settings: CallCenterSettings;
  branches: CallCenterBranchOption[];
  paymentTypes: CallCenterPaymentType[];
  /**
   * Inv_PaymentType.Code meaning "paid with a gift card", or null when the
   * tenant has none. The settle dialog needs it to tell that tender apart —
   * picking a card must ask for a number and check the balance.
   */
  giftCardPaymentCode: number | null;
  taxPercentage: number;
  currency: { code: string; precision: number };
  serverTime: string;
}

export interface CallCenterOrder {
  id: number;
  /** Inv_Receipt_h.ID — THE order. Everything else here describes it. */
  receiptId: number;
  entryNo: number | null;
  status: CallCenterStatus;
  branchId: number;
  branchName: string | null;
  orderType: number | null;

  customerId: number | null;
  customerName: string | null;
  customerNameAr: string | null;
  customerPhone: string | null;
  addressId: number | null;
  addressSummary: string | null;

  agentUserId: number;
  agentName: string | null;

  totalAmount: number;
  deliveryFee: number;
  itemCount: number;

  isPrepaid: boolean;
  paymentTypeId: number | null;
  paymentTypeLabel: string | null;
  settledByUserId: number | null;
  settledByName: string | null;
  settledAt: string | null;

  driverId: number | null;
  driverName: string | null;
  /**
   * The same driver, in Arabic. Resolved live from Security_Users, falling
   * back to the one denormalised name on the order when that driver is gone.
   * Both travel so a screen can show the reader's language rather than the
   * language of whoever happened to dispatch.
   */
  driverNameAr: string | null;

  placedAt: string;
  promisedAt: string | null;
  /**
   * When the customer was promised the order — `Inv_Receipt_h.CustomerDeliverDate`
   * read off the receipt, so the agent dashboard, the branch's Pending Orders and
   * the Delivery workspace all show the one time. Wall-clock digits (a trailing
   * 'Z' does not mean UTC): render with `formatReceiptDateTime`, never
   * `toLocaleTimeString`. Null on an order taken before the field existed.
   */
  deliveryAt: string | null;
  preparingAt: string | null;
  readyAt: string | null;
  outAt: string | null;
  deliveredAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;

  note: string | null;
}

export interface CallCenterOrderLine {
  itemId: number | null;
  name: string | null;
  nameAr: string | null;
  qty: number;
  unitPrice: number;
  lineTotal: number;
  notes: string | null;
}

export interface CallCenterOrderEvent {
  id: number;
  fromStatus: CallCenterStatus | null;
  toStatus: CallCenterStatus;
  source: CallCenterEventSource;
  actorUserId: number | null;
  actorName: string | null;
  note: string | null;
  createdAt: string;
}

export interface CallCenterOrderDetail extends CallCenterOrder {
  lines: CallCenterOrderLine[];
  events: CallCenterOrderEvent[];
}

export interface CallCenterOrderListResponse {
  orders: CallCenterOrder[];
  total: number;
  page: number;
  limit: number;
  /**
   * The clock every live waiting time is measured against.
   *
   * A call-centre PC or a counter tablet whose clock is four minutes fast would
   * otherwise show every order as four minutes late from the instant it was
   * placed — and staff who learn to distrust one number learn to ignore the
   * colour bands too. Clients compute elapsed as
   * (serverTime − placedAt) + (local ticks since this response landed).
   */
  serverTime: string;
  warnSeconds: number;
  criticalSeconds: number;
}

export interface CallCenterKpis {
  ordersToday: number;
  salesToday: number;
  averageTicket: number;
  openOrders: number;
  lateOrders: number;
  cancelledToday: number;
  /** Send → kitchen-ready, seconds. null when nothing completed in the window. */
  avgPrepSeconds: number | null;
  /** Send → handed over, seconds. */
  avgFulfilSeconds: number | null;
  cancellationRate: number;
}

export interface CallCenterDashboard {
  kpis: CallCenterKpis;
  byStatus: Array<{ status: CallCenterStatus; count: number; amount: number }>;
  byBranch: Array<{
    branchId: number;
    branchName: string | null;
    orders: number;
    amount: number;
    open: number;
    avgFulfilSeconds: number | null;
  }>;
  byHour: Array<{ hour: number; orders: number; amount: number }>;
  byPayment: Array<{
    paymentTypeId: number | null;
    label: string | null;
    orders: number;
    amount: number;
  }>;
  topItems: Array<{ itemId: number; name: string | null; qty: number; amount: number }>;
  agents: Array<{
    userId: number;
    name: string | null;
    orders: number;
    amount: number;
    cancelled: number;
  }>;
  serverTime: string;
}

export type CallCenterStreamEvent =
  | { type: 'hello'; serverTime: string }
  | { type: 'ping'; serverTime: string }
  | { type: 'order'; serverTime: string; order: CallCenterOrder }
  | { type: 'removed'; serverTime: string; orderId: number };

// ── Display helpers ───────────────────────────────────────────────────────

/**
 * Bilingual labels for every status, in one place.
 *
 * Four surfaces render these — the agent dashboard, the branch pending list,
 * the branch monitor and the admin screens — and a status that reads "Ready" on
 * one and "Prepared" on another is a support call waiting to happen.
 */
export const CC_STATUS_LABELS: Record<CallCenterStatus, { en: string; ar: string }> = {
  new: { en: 'New', ar: 'جديد' },
  preparing: { en: 'Preparing', ar: 'قيد التحضير' },
  ready: { en: 'Ready', ar: 'جاهز' },
  out_for_delivery: { en: 'Out for delivery', ar: 'خرج للتوصيل' },
  delivered: { en: 'Delivered', ar: 'تم التسليم' },
  cancelled: { en: 'Cancelled', ar: 'ملغي' },
};

/**
 * Which SLA band an order is in, given how long it has been open.
 *
 * Terminal orders are always 'ok': an order delivered forty minutes ago is not
 * late, it is finished, and colouring the archive red teaches everyone to stop
 * looking at the colour.
 */
export function ccDelayBand(
  status: CallCenterStatus,
  elapsedSeconds: number,
  warnSeconds: number,
  criticalSeconds: number,
): 'ok' | 'warn' | 'critical' {
  if (status === 'delivered' || status === 'cancelled') return 'ok';
  if (elapsedSeconds >= criticalSeconds) return 'critical';
  if (elapsedSeconds >= warnSeconds) return 'warn';
  return 'ok';
}

/** `m:ss` under an hour, `h:mm:ss` beyond it. Never a bare seconds count. */
export function ccFormatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(s / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = s % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}
